// =============================================================
//  DocumentDetailScreen — Détail complet d'un document
//  Affiche TOUS les champs réels selon le type (concours, bords, documents) :
//    Concours : sigle, nomComplet, description, niveau, langue, typeConcours,
//               hasGroups, groups[], documents[], arretes[], coverImage,
//               restrictionTelechargement, displayOrder, status, createdAt
//    Bords    : titre, nomDocument, description, image, documents[],
//               documentsAnglais[], documentsOptionnels[], liens[],
//               prix, prixInitial, reductionPourcent, prixFinal,
//               nombrePages, echantillons[], langue, feuilletage,
//               restrictionTelechargement, displayOrder, status, createdAt
//    Simple   : titre, nomDocument, description, document, coverImage,
//               liens[], restrictionTelechargement, status, createdAt
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet, Linking, Image as RNImage } from 'react-native';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { db } from '../private/firebaseConfig';
import DocumentLifecycleService from '../services/DocumentLifecycleService';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Divider } from './web-ui/kit';
import { colors, spacing, radius } from './web-ui/theme';

const fmtPrice = (p) => {
  const n = Number(p) || 0;
  if (!n) return 'Gratuit';
  return new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('fr-FR');
};

const openUrl = (url) => {
  if (!url) return;
  if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
  else Linking.openURL(url);
};

const yesNo = (v) => (v ? 'Oui' : 'Non');

// Récupère une URL de document depuis un objet (peut être string ou {document})
const docUrl = (entry) => {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (typeof entry.document === 'string') return entry.document;
  if (typeof entry.url === 'string') return entry.url;
  return null;
};

export default function DocumentDetailScreen({ route, navigation }) {
  const initial = route?.params?.document || {};
  const [document, setDocument] = useState(initial);
  const [loading, setLoading] = useState(false);

  const collectionName = document.type || 'documents';
  const isActive = document.status === 'active';
  const title = document.sigle || document.titre || document.nomComplet || 'Sans titre';

  const toggleStatus = async () => {
    setLoading(true);
    try {
      const newStatus = isActive ? 'inactive' : 'active';
      await updateDoc(doc(db, collectionName, document.id), { status: newStatus });
      setDocument((p) => ({ ...p, status: newStatus }));
    } catch (e) {
      console.log('toggle error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      // Un document déjà acheté n'est jamais détruit : on archive à la place
      // pour préserver l'accès des utilisateurs qui ont payé.
      const safety = await DocumentLifecycleService.checkDeletionSafety(document.id);

      // Vérification impossible (réseau) → on ne prend aucun risque.
      if (!safety.canDelete && safety.purchaseCount === 0) {
        window.alert(safety.reason);
        return;
      }

      const hasPurchases = safety.purchaseCount > 0;
      const message = hasPurchases
        ? `${safety.reason}\n\nArchiver "${title}" ?`
        : `Aucun achat pour "${title}". Le supprimer définitivement ? Action irréversible.`;
      if (!window.confirm(message)) return;

      const result = await DocumentLifecycleService.safeDelete(document.id, collectionName);
      if (result.action === 'failed') {
        window.alert(result.message);
        return;
      }
      navigation.goBack();
    } catch (e) {
      console.log('delete error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  // ---- Sections par type ----
  const isConcours = collectionName === 'concours';
  const isBords = collectionName === 'bords';
  const isSimple = collectionName === 'documents';

  return (
    <AdminLayout title={title} subtitle={`Détail ${collectionName} · ${document.id}`}>
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
        <Button
          variant="secondary"
          icon="pencil-outline"
          onPress={() => navigation.navigate('EditDocumentScreen', { document })}
        >
          Modifier
        </Button>
        <Button
          variant={isActive ? 'secondary' : 'primary'}
          icon={isActive ? 'eye-off-outline' : 'eye-outline'}
          loading={loading}
          onPress={toggleStatus}
        >
          {isActive ? 'Désactiver' : 'Activer'}
        </Button>
        <Button variant="danger" icon="trash-can-outline" loading={loading} onPress={handleDelete}>
          Supprimer
        </Button>
      </View>

      {/* En-tête principal */}
      <Card>
        <View style={{ flexDirection: 'row', gap: spacing.lg, alignItems: 'flex-start' }}>
          {document.coverImage || document.image ? (
            <RNImage
              source={{ uri: document.coverImage || document.image }}
              style={styles.coverImg}
              resizeMode="cover"
            />
          ) : (
            <View style={styles.cover}>
              <Text style={styles.coverText}>{title.charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.title}>{title}</Text>
            {!!document.nomComplet && document.nomComplet !== title && (
              <Text style={styles.subtitle}>{document.nomComplet}</Text>
            )}
            {!!document.nomDocument && (
              <Text style={styles.subtitle}>Fichier : {document.nomDocument}</Text>
            )}
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <Badge variant={isActive ? 'success' : 'warning'}>
                {isActive ? 'Actif' : 'Inactif'}
              </Badge>
              <Badge variant="primary">{collectionName}</Badge>
              {!!document.langue && <Badge variant="info">{document.langue}</Badge>}
              {!!document.niveau && <Badge variant="info">{document.niveau}</Badge>}
              {!!document.typeConcours && <Badge variant="info">{document.typeConcours}</Badge>}
              {document.gratuit === true && <Badge variant="success">Gratuit</Badge>}
              {document.restrictionTelechargement && (
                <Badge variant="warning" icon="download-off-outline">Téléchargement bloqué</Badge>
              )}
            </View>
          </View>
        </View>

        <Divider />

        {!!document.description && (
          <>
            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.body}>{document.description}</Text>
            <Divider />
          </>
        )}

        {/* Infos de base */}
        <Text style={styles.sectionTitle}>Informations</Text>
        <View style={styles.grid}>
          <Info label="ID Firestore" value={document.id} />
          <Info label="Type" value={collectionName} />
          <Info label="Statut" value={document.status || 'inconnu'} />
          {document.displayOrder != null && (
            <Info label="Ordre d'affichage" value={`#${document.displayOrder}`} />
          )}
          <Info label="Date de création" value={fmtDate(document.createdAt)} />
          {!!document.updatedAt && <Info label="Dernière modification" value={fmtDate(document.updatedAt)} />}
          {!!document.langue && <Info label="Langue" value={document.langue} />}
          {!!document.niveau && <Info label="Niveau" value={document.niveau} />}
          {!!document.typeConcours && <Info label="Type de concours" value={document.typeConcours} />}
          {document.restrictionTelechargement !== undefined && (
            <Info label="Restriction téléchargement" value={yesNo(document.restrictionTelechargement)} />
          )}
        </View>

        {/* Tarification (bords) */}
        {(document.prix != null || document.prixFinal != null) && (
          <>
            <Divider />
            <Text style={styles.sectionTitle}>Tarification</Text>
            <View style={styles.grid}>
              {document.prix != null && <Info label="Prix" value={fmtPrice(document.prix)} />}
              {document.prixInitial != null && <Info label="Prix initial" value={fmtPrice(document.prixInitial)} />}
              {document.prixFinal != null && <Info label="Prix final" value={fmtPrice(document.prixFinal)} />}
              {document.reductionPourcent != null && (
                <Info label="Réduction" value={`${document.reductionPourcent}%`} />
              )}
              {document.nombrePages != null && <Info label="Nombre de pages" value={document.nombrePages} />}
            </View>
          </>
        )}
      </Card>

      {/* ===== CONCOURS ===== */}
      {isConcours && Array.isArray(document.arretes) && document.arretes.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Arrêtés du concours ({document.arretes.length})
          </Text>
          {document.arretes.map((a, i) => {
            const url = docUrl(a) || a.url;
            return (
              <View key={a.id || i} style={styles.subCard}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.subTitle}>{a.titre || `Arrêté ${i + 1}`}</Text>
                  {!!a.annee && <Text style={styles.subMeta}>Année : {a.annee}</Text>}
                  {!!url && <Text style={styles.subMeta} numberOfLines={1}>{url}</Text>}
                </View>
                {!!url && (
                  <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                    Ouvrir
                  </Button>
                )}
              </View>
            );
          })}
        </Card>
      )}

      {/* Groupes (concours hasGroups) */}
      {isConcours && document.hasGroups && Array.isArray(document.groups) && document.groups.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Groupes ({document.groups.length})
          </Text>
          {document.groups.map((g, gi) => (
            <View key={g.id || gi} style={styles.group}>
              <Text style={styles.groupTitle}>{g.nom || g.name || `Groupe ${gi + 1}`}</Text>
              {Array.isArray(g.documents) && g.documents.length > 0 ? (
                g.documents.map((d, di) => {
                  const url = docUrl(d);
                  return (
                    <View key={di} style={styles.subCard}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.subTitle}>{d.titre || `Document ${di + 1}`}</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                          {!!d.annee && <Text style={styles.subMeta}>Année : {d.annee}</Text>}
                          {!!d.filiere && <Text style={styles.subMeta}>Filière : {d.filiere}</Text>}
                          {!!d.nombrePages && <Text style={styles.subMeta}>Pages : {d.nombrePages}</Text>}
                          {d.gratuit && <Badge variant="success">Gratuit</Badge>}
                          {d.prix != null && <Text style={styles.subMeta}>Prix : {fmtPrice(d.prix)}</Text>}
                          {d.prixInitial != null && d.prixInitial !== d.prix && (
                            <Text style={styles.subMeta}>Prix initial : {fmtPrice(d.prixInitial)}</Text>
                          )}
                          {d.reductionPourcent != null && parseFloat(d.reductionPourcent) > 0 && (
                            <Text style={styles.subMeta}>Réduction : {d.reductionPourcent}%</Text>
                          )}
                        </View>
                      </View>
                      {!!url && (
                        <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                          Ouvrir
                        </Button>
                      )}
                    </View>
                  );
                })
              ) : (
                <Text style={styles.subMeta}>Aucun document dans ce groupe</Text>
              )}
            </View>
          ))}
        </Card>
      )}

      {/* Documents (concours sans groupes / bords / simple) */}
      {!document.hasGroups && Array.isArray(document.documents) && document.documents.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            {isConcours ? 'Documents du concours' : 'Documents inclus'} ({document.documents.length})
          </Text>
          {document.documents.map((d, i) => {
            const url = docUrl(d);
            const isObj = typeof d === 'object';
            return (
              <View key={i} style={styles.subCard}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.subTitle}>
                    {isObj ? (d.titre || `Document ${i + 1}`) : `Document ${i + 1}`}
                  </Text>
                  {isObj && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {!!d.annee && <Text style={styles.subMeta}>Année : {d.annee}</Text>}
                      {!!d.filiere && <Text style={styles.subMeta}>Filière : {d.filiere}</Text>}
                      {!!d.nombrePages && <Text style={styles.subMeta}>Pages : {d.nombrePages}</Text>}
                      {d.gratuit && <Badge variant="success">Gratuit</Badge>}
                      {d.prix != null && <Text style={styles.subMeta}>Prix : {fmtPrice(d.prix)}</Text>}
                      {d.prixInitial != null && d.prixInitial !== d.prix && (
                        <Text style={styles.subMeta}>Prix initial : {fmtPrice(d.prixInitial)}</Text>
                      )}
                      {d.reductionPourcent != null && parseFloat(d.reductionPourcent) > 0 && (
                        <Text style={styles.subMeta}>Réduction : {d.reductionPourcent}%</Text>
                      )}
                    </View>
                  )}
                  {!!url && (
                    <Text style={[styles.subMeta, { marginTop: 4 }]} numberOfLines={1}>{url}</Text>
                  )}
                </View>
                {!!url && (
                  <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                    Ouvrir
                  </Button>
                )}
              </View>
            );
          })}
        </Card>
      )}

      {/* Bords — version anglaise */}
      {isBords && Array.isArray(document.documentsAnglais) && document.documentsAnglais.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Version anglaise ({document.documentsAnglais.length})
          </Text>
          {document.documentsAnglais.map((d, i) => {
            const url = typeof d === 'string' ? d : docUrl(d);
            return (
              <View key={i} style={styles.subCard}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.subTitle}>Document EN {i + 1}</Text>
                  {!!url && <Text style={styles.subMeta} numberOfLines={1}>{url}</Text>}
                </View>
                {!!url && (
                  <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                    Ouvrir
                  </Button>
                )}
              </View>
            );
          })}
        </Card>
      )}

      {/* Bords — documents optionnels */}
      {isBords && Array.isArray(document.documentsOptionnels) && document.documentsOptionnels.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Documents optionnels ({document.documentsOptionnels.length})
          </Text>
          {document.documentsOptionnels.map((d, i) => {
            const url = docUrl(d);
            return (
              <View key={i} style={styles.subCard}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.subTitle}>{d.titre || `Document optionnel ${i + 1}`}</Text>
                  {d.prix != null && <Text style={styles.subMeta}>Prix : {fmtPrice(d.prix)}</Text>}
                  {!!url && <Text style={styles.subMeta} numberOfLines={1}>{url}</Text>}
                </View>
                {!!url && (
                  <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                    Ouvrir
                  </Button>
                )}
              </View>
            );
          })}
        </Card>
      )}

      {/* Bords — feuilletage */}
      {isBords && !!document.feuilletage && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>Feuilletage</Text>
          <View style={styles.subCard}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.subTitle}>Aperçu du document</Text>
              <Text style={styles.subMeta} numberOfLines={1}>{document.feuilletage}</Text>
            </View>
            <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(document.feuilletage)}>
              Ouvrir
            </Button>
          </View>
        </Card>
      )}

      {/* Bords — échantillons (images) */}
      {isBords && Array.isArray(document.echantillons) && document.echantillons.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Échantillons ({document.echantillons.length})
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
            {document.echantillons.map((src, i) => (
              <RNImage
                key={i}
                source={{ uri: src }}
                style={styles.sample}
                resizeMode="cover"
              />
            ))}
          </View>
        </Card>
      )}

      {/* Bords/Simple — liens externes */}
      {Array.isArray(document.liens) && document.liens.length > 0 && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>
            Liens externes ({document.liens.length})
          </Text>
          {document.liens.map((l, i) => {
            const url = typeof l === 'string' ? l : l?.url || l?.lien;
            const label = typeof l === 'object' ? (l.titre || l.label) : null;
            return (
              <View key={i} style={styles.subCard}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.subTitle}>{label || `Lien ${i + 1}`}</Text>
                  {!!url && <Text style={styles.subMeta} numberOfLines={1}>{url}</Text>}
                </View>
                {!!url && (
                  <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(url)}>
                    Ouvrir
                  </Button>
                )}
              </View>
            );
          })}
        </Card>
      )}

      {/* Simple — document principal */}
      {isSimple && !!document.document && (
        <Card style={styles.spacer}>
          <Text style={styles.sectionTitle}>Document principal</Text>
          <View style={styles.subCard}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.subTitle}>{document.nomDocument || 'Fichier'}</Text>
              <Text style={styles.subMeta} numberOfLines={1}>{document.document}</Text>
            </View>
            <Button size="sm" variant="secondary" icon="open-in-new" onPress={() => openUrl(document.document)}>
              Ouvrir
            </Button>
          </View>
        </Card>
      )}
    </AdminLayout>
  );
}

function Info({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={1}>{value ?? '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  spacer: { marginTop: spacing.lg },
  cover: {
    width: 96,
    height: 130,
    borderRadius: radius.md,
    backgroundColor: colors.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  coverText: { color: colors.primary, fontWeight: '800', fontSize: 40 },
  coverImg: {
    width: 96,
    height: 130,
    borderRadius: radius.md,
    backgroundColor: colors.surfaceAlt,
  },
  title: { fontSize: 22, fontWeight: '700', color: colors.text, letterSpacing: -0.3 },
  subtitle: { fontSize: 14, color: colors.textSecondary, marginTop: 4 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.md },
  body: { fontSize: 14, color: colors.textSecondary, lineHeight: 21 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase' },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
  subCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
    marginBottom: spacing.sm,
  },
  subTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  subMeta: { fontSize: 12, color: colors.textSecondary },
  group: {
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
    marginBottom: spacing.md,
  },
  groupTitle: { fontSize: 15, fontWeight: '700', color: colors.primary, marginBottom: spacing.sm },
  sample: {
    width: 120,
    height: 120,
    borderRadius: 10,
    backgroundColor: colors.surfaceAlt,
  },
});
